import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import type { LessonPage } from "@/integrations/supabase/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
// Lesson onboarding removed
import LessonContent from "@/components/lessons/LessonContent";
import MascotDisplay from "@/components/MascotDisplay";
import { SimulationRenderer } from '@/components/simulations';
import { parseAvatarState, type AvatarState, fetchUserAvatarConfig } from "@/lib/avatar";
import * as tts from "@mintplex-labs/piper-tts-web";
import SEO from "@/components/SEO";

type MascotState = "idle" | "correct" | "incorrect" | "celebrating" | "thinking";
import { LessonComplete } from "@/components/lessons/LessonComplete";

const getGradeLevel = (slug: string) => {
  const match = slug.match(/g(\d+)/i);
  if (match) return match[1];
  if (slug.includes("univ")) return "University";
  return "10";
};
import {
  ArrowLeft,
  Award,
  BookOpen,
  X,
  Heart,
  HeartCrack,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { HelixLoader } from "@/components/ui/helix-loader";
import { triggerLevelUpConfetti, triggerLessonCompleteConfetti } from "@/utils/confettiEffects";
import { logQuizCompleted } from "@/utils/activityLogging";

class PageErrorBoundary extends React.Component<
  { children: React.ReactNode; pageType: string },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[PageErrorBoundary] Caught error rendering ${this.props.pageType}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full max-w-lg mx-auto my-8 p-6 rounded-2xl border-2 border-red-500/50 bg-red-500/10 text-white space-y-4 shadow-xl">
          <div className="flex items-center gap-3">
            <span className="text-3xl">⚠️</span>
            <h3 className="text-xl font-bold text-red-400">Rendering Error</h3>
          </div>
          <p className="text-white/80 leading-relaxed text-sm">
            Something went wrong while displaying this page type: <span className="font-semibold font-mono text-amber-300">{this.props.pageType}</span>.
          </p>
          <div className="bg-black/30 p-3 rounded-lg text-xs font-mono text-red-300 overflow-x-auto max-h-32">
            {this.state.error?.toString()}
          </div>
          <p className="text-xs text-white/50">
            You can still use the navigation controls below to skip to another page or quit.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

class SimulationErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[SimulationErrorBoundary] Caught error rendering simulation:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-red-950/20 border border-red-500/20 rounded-xl space-y-3">
          <span className="text-2xl text-red-400">⚙️ Simulation Crash</span>
          <p className="text-xs text-white/60 max-w-[280px]">
            The interactive simulation for this page failed to initialize.
          </p>
          <div className="bg-black/40 p-2 rounded text-[10px] font-mono text-red-300 w-full overflow-x-auto max-h-20 text-left">
            {this.state.error?.toString()}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

interface LessonExample {
  title: string;
  description: string;
}

// Fact page type (not in supabase types yet)
interface FactPage {
  type: 'fact';
  text: string;
}

type LessonPageNew = LessonPage | FactPage;

interface Lesson {
  id: string;
  title: string;
  content: string | null;
  pages?: LessonPageNew[] | null;
  slug?: string;
  animations?: unknown[];
  examples?: LessonExample[];
  quiz?: QuizQuestion[];
  xp_reward: number;
  order_index: number;
  chapter?: string;
  is_placeholder?: boolean;
}

interface QuizQuestion {
  question: string;
  options: string[];
  correct?: number;
  correctAnswer?: number;
  explanation?: string;
}

function parseLessonPages(content: string | null | undefined, fallbackTitle: string): LessonPage[] {
  const fallbackPage: LessonPage = {
    type: "read",
    title: fallbackTitle,
    content: content ?? "",
  };

  if (!content || !content.trim()) {
    return [fallbackPage];
  }

  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed as LessonPage[];
    }
  } catch {
    // Not JSON, fall back to a single read page.
  }

  return [fallbackPage];
}

class MascotErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("[LessonPlayer] Mascot error caught:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-24 h-24 rounded-full bg-slate-800/50 flex items-center justify-center text-[10px] text-slate-500 font-medium">
          Mascot Offline
        </div>
      );
    }
    return this.props.children;
  }
}

export default function LessonPlayer() {
  const { courseSlug, lessonSlug, pageIndex } = useParams();
  const navigate = useNavigate();
  const [resolvedCourseSlug, setResolvedCourseSlug] = useState<string>("");
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [courseLessons, setCourseLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completingLesson, setCompletingLesson] = useState(false);
  const [userProgress, setUserProgress] = useState<Array<{ lesson_id: string; status: string }>>([]);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [recommendedSection, setRecommendedSection] = useState<string | undefined>();

  // Chapter quiz state (multi-step quiz at end of chapter)
  const [showChapterQuiz, setShowChapterQuiz] = useState(false);
  const [chapterQuizCompleted, setChapterQuizCompleted] = useState<Set<string>>(new Set());
  const [currentChapterQuizQuestion, setCurrentChapterQuizQuestion] = useState(0);
  const [chapterQuizAnswers, setChapterQuizAnswers] = useState<Record<number, number>>({});
  const [chapterQuizResults, setChapterQuizResults] = useState<Record<number, boolean>>({});
  const [chapterQuizQuestions, setChapterQuizQuestions] = useState<QuizQuestion[]>([]);

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  // isMathJaxLoaded tracks whether MathJax script has loaded
  const [isMathJaxLoaded, setIsMathJaxLoaded] = useState(false);
  const [pages, setPages] = useState<LessonPageNew[]>([]);
  const [currentPage, setCurrentPage] = useState(() => {
    if (!pageIndex) return 0;
    const parsed = parseInt(pageIndex, 10);
    return isNaN(parsed) || parsed < 1 ? 0 : parsed - 1;
  });

  // Sync currentPage to URL when it changes
  useEffect(() => {
    if (lessonSlug) {
      navigate(`/lesson/${lessonSlug}/${currentPage + 1}`, { replace: true });
    }
  }, [currentPage, lessonSlug, navigate]);

  useEffect(() => {
    matchShuffledTerms.current = [];
    matchShuffledDefs.current = [];
    setMatchSelectedTerm(null);
    setMatchSelectedDef(null);
    setMatchMatched(new Set());
    setMatchWrong(false);
  }, [currentPage]);
  const [quizAnswer, setQuizAnswer] = useState<number | null>(null);
  const [quizRevealed, setQuizRevealed] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [piperLoading, setPiperLoading] = useState(false);
  const piperAudioRef = useRef<HTMLAudioElement | null>(null);
  const [mascotState, setMascotState] = useState<MascotState>("idle");
  const [mascotVariant, setMascotVariant] = useState(1);
  const [hearts, setHearts] = useState(5);
  const [lessonFailed, setLessonFailed] = useState(false);
  const [streakCount, setStreakCount] = useState(0);
  const [showStreakBanner, setShowStreakBanner] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [lessonComplete, setLessonComplete] = useState(false);
  const [completionData, setCompletionData] = useState<any | null>(null);
  const [maxCombo, setMaxCombo] = useState(0);
  const [authResolved, setAuthResolved] = useState(false);
  const [matchSelectedTerm, setMatchSelectedTerm] = useState<string|null>(null);
  const [matchSelectedDef, setMatchSelectedDef] = useState<string|null>(null);
  const [matchMatched, setMatchMatched] = useState<Set<string>>(new Set());
  const [matchWrong, setMatchWrong] = useState<boolean>(false);
  const matchShuffledTerms = useRef<string[]>([]);
  const matchShuffledDefs = useRef<string[]>([]);

  const [orderCurrent, setOrderCurrent] = useState<number[]>([]);

  const currentPageData = pages[currentPage] ?? pages[0] ?? null;
  const totalPages = pages.length || 1;
  const isLastPage = currentPage >= totalPages - 1;
  const isQuizPage = currentPageData?.type === "quiz" || currentPageData?.type === "order";
  const isReadPage = currentPageData?.type === "read";
  const isMatchPage = currentPageData?.type === "match";
  const matchPairsCount = (currentPageData as any)?.pairs?.length || 0;
  const isMatchComplete = isMatchPage ? (matchMatched.size === matchPairsCount && matchPairsCount > 0) : true;
  const canAdvancePage = isMatchComplete && (!isQuizPage || quizRevealed);

  useEffect(() => {
    if (currentPageData?.type === 'order') {
      const items = (currentPageData as any).items || [];
      const initialIndices = items.map((_: any, idx: number) => idx);
      
      let shuffled = [...initialIndices];
      const correct = (currentPageData as any).correctOrder || [];
      
      const shuffle = (arr: number[]) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };

      let attempts = 0;
      do {
        shuffled = shuffle(initialIndices);
        attempts++;
      } while (
        attempts < 10 && 
        shuffled.length > 1 && 
        shuffled.every((val, index) => val === correct[index])
      );

      setOrderCurrent(shuffled);
    } else {
      setOrderCurrent([]);
    }
  }, [currentPage, currentPageData]);

  const handleMoveOrderItem = (index: number, direction: 'up' | 'down') => {
    if (quizRevealed) return;
    const newOrder = [...orderCurrent];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    
    const temp = newOrder[index];
    newOrder[index] = newOrder[targetIndex];
    newOrder[targetIndex] = temp;
    setOrderCurrent(newOrder);
  };

  const [avatarState, setAvatarState] = useState<AvatarState>({
    elementN: 6,
    color: '#60A5FA',
    accessory: 'None',
  });

  useEffect(() => {
    if (!userId) return;
    const fetchAvatar = async () => {
      try {
        const config = await fetchUserAvatarConfig(userId);
        setAvatarState(config);
      } catch (err) {
        console.error("Error loading avatar in LessonPlayer:", err);
      }
    };
    fetchAvatar();
  }, [userId]);

  useEffect(() => {
    const currentPageData = pages[currentPage];
    if (currentPageData?.type === "read") {
      setMascotState("idle");
      const timer = setTimeout(() => {
        setMascotState("thinking");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [pages, currentPage]);

  const speechTokenRef = useRef(0);

  const incrementQuestionsAsked = async (count: number) => {
    if (!userId || count <= 0) return;
    try {
      const { data } = await supabase.from("user_stats").select("questions_asked").eq("user_id", userId).maybeSingle();
      const current = data?.questions_asked ?? 0;
      await supabase.from("user_stats").update({ questions_asked: current + count }).eq("user_id", userId);
    } catch {
      // ignore
    }
  };

  // Load MathJax script dynamically when needed
  useEffect(() => {
    if (lesson && lesson.content && (lesson.content.includes('math') || lesson.content.includes('formula'))) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
      script.onload = () => setIsMathJaxLoaded(true);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup MathJax script when component unmounts
      const mathjaxScript = document.querySelector('script[src*="https://cdn.jsdelivr.net/npm/mathjax@3"]');
      if (mathjaxScript && mathjaxScript.parentNode) {
        mathjaxScript.parentNode.removeChild(mathjaxScript);
      }
      setIsMathJaxLoaded(false);
    };
  }, [lesson?.id, lesson?.content]);

  useEffect(() => {
    if (!lesson) return;

    // Use lesson.pages if available, otherwise fall back to parsing content
    let lessonPages: LessonPageNew[] = [];
    if (lesson.pages && Array.isArray(lesson.pages) && lesson.pages.length > 0) {
      lessonPages = lesson.pages;
    } else {
      // Fallback: wrap lesson.content in a single read page
      lessonPages = [{ type: 'read', title: lesson.title, content: lesson.content || '' }];
    }
    setPages(lessonPages);
    setCurrentPage(0);
    setQuizAnswer(null);
    setQuizRevealed(false);
    setAudioPlaying(false);
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    speechTokenRef.current += 1;
  }, [lesson?.id, lesson?.pages, lesson?.title]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Load chapter quiz questions when quiz is shown
  useEffect(() => {
    if (showChapterQuiz && lesson?.chapter && courseLessons.length > 0) {
      const loadChapterQuiz = async () => {
        const questions = await getChapterQuiz();
        setChapterQuizQuestions(questions);
        setCurrentChapterQuizQuestion(0);
        setChapterQuizAnswers({});
        setChapterQuizResults({});
      };
      loadChapterQuiz();
    }
  }, [showChapterQuiz, lesson?.chapter]);

  useEffect(() => {
    const init = async () => {
      setAuthResolved(false);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUserId(session.user.id);
          await loadLesson(session.user.id);
        } else {
          navigate('/');
        }
      } finally {
        setAuthResolved(true);
      }
    };
    init();
  }, [courseSlug, lessonSlug]);

  useEffect(() => {
    if (!userId) return;
    const loadHearts = async () => {
      const { data, error } = await supabase.functions.invoke('lessons', {
        body: { action: 'refill_hearts' }
      });

      if (error) {
        console.error('[LessonPlayer] Error loading hearts:', error);
        return;
      }

      setHearts(data?.hearts ?? 5);
    };
    loadHearts();
  }, [userId]);

  useEffect(() => {
    if (hearts === 0) {
      setLessonFailed(true);
    }
  }, [hearts]);

  const loadLesson = async (uid: string) => {
    try {
      let slugToUse = courseSlug;

      if (!slugToUse && lessonSlug) {
        // Fetch lesson from supabase to resolve course_id
        const { data: lessonRec } = await supabase
          .from('lessons')
          .select('course_id')
          .eq('slug', lessonSlug)
          .maybeSingle();

        if (lessonRec?.course_id) {
          const { data: courseRec } = await supabase
            .from('courses')
            .select('slug')
            .eq('id', lessonRec.course_id)
            .maybeSingle();
          
          if (courseRec?.slug) {
            slugToUse = courseRec.slug;
          }
        }
      }

      // Fallback based on lessonSlug prefix if database lookup did not resolve it
      if (!slugToUse && lessonSlug) {
        if (lessonSlug.startsWith('astro-')) slugToUse = 'astrophysics';
        else if (lessonSlug.startsWith('phys-')) slugToUse = 'physics';
        else if (lessonSlug.startsWith('quantum-')) slugToUse = 'quantum';
        else if (lessonSlug.startsWith('astronomy-')) slugToUse = 'astronomy';
      }

      if (slugToUse) {
        setResolvedCourseSlug(slugToUse);
      }

      const { data: courseData } = await supabase.functions.invoke('courses', {
        body: { slug: slugToUse || 'astrophysics' }
      });

      if (courseData?.lessons && Array.isArray(courseData.lessons)) {
        const sortedLessons = [...courseData.lessons].sort((a: Lesson, b: Lesson) => a.order_index - b.order_index);
        setCourseLessons(sortedLessons);

        const foundLesson = courseData.lessons.find((l: Lesson) => l.slug === lessonSlug);

        if (foundLesson) {
          const lessonIndex = sortedLessons.findIndex((l: Lesson) => l.id === foundLesson.id);

          const { data: lessonData, error: lessonError } = await supabase.functions.invoke('lessons', {
            body: { id: foundLesson.id }
          });

          if (lessonError) {
            console.error('[LessonPlayer] Error loading lesson:', lessonError);
            toast.error('Failed to load lesson content');
            return;
          }

          if (lessonData) {
            setLesson(lessonData);

            // Load all user progress for this course
            await loadProgress(uid);

            if (lessonData.is_placeholder) {
              setIsCompleted(false);
              setShowOnboarding(false);
              setRecommendedSection(undefined);
            } else {
              // Check onboarding status (some deployments don't have onboarding_* columns on user_progress)
              let progressData: any = null;
              let onboardingFieldsSupported = true;

              try {
                const { data, error } = await supabase
                  .from("user_progress")
                  .select("status, onboarding_completed, recommended_starting_section")
                  .eq("user_id", uid)
                  .eq("lesson_id", lessonData.id)
                  .maybeSingle();

                if (error) {
                  onboardingFieldsSupported = false;
                } else {
                  progressData = data ?? null;
                }
              } catch {
                onboardingFieldsSupported = false;
              }

              if (!onboardingFieldsSupported) {
                try {
                  const { data } = await supabase
                    .from("user_progress")
                    .select("status")
                    .eq("user_id", uid)
                    .eq("lesson_id", lessonData.id)
                    .maybeSingle();
                  progressData = data ?? null;
                } catch {
                  progressData = null;
                }

                setIsCompleted(progressData?.status === "completed");
                setShowOnboarding(false);
                setRecommendedSection(undefined);
              } else {
                setIsCompleted(progressData?.status === "completed");

                // Check if onboarding is needed
                if (progressData) {
                  if (!progressData.onboarding_completed) {
                    setShowOnboarding(true);
                  }
                  setRecommendedSection(progressData.recommended_starting_section || undefined);
                } else {
                  // No progress record yet, create one and show onboarding
                  try {
                    await supabase.from("user_progress").insert({
                      user_id: uid,
                      lesson_id: lessonData.id,
                      status: "in_progress",
                      onboarding_completed: false,
                    });
                    setShowOnboarding(true);
                  } catch {
                    setShowOnboarding(false);
                  }
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
      setCheckingOnboarding(false);
    }
  };

  const loadProgress = async (uid: string) => {
    try {
      const { data: progressData } = await supabase
        .from('user_progress')
        .select('lesson_id, status')
        .eq('user_id', uid);

      if (progressData) {
        setUserProgress(progressData);
      }
    } catch (error) {
      console.error('[LessonPlayer] Error loading progress:', error);
    }
  };

  const getNextLesson = () => {
    if (!lesson || courseLessons.length === 0) return null;
    const currentIndex = courseLessons.findIndex(l => l.id === lesson.id);
    if (currentIndex === -1) return null;

    const upcomingLessons = courseLessons.slice(currentIndex + 1);
    return upcomingLessons.find(l => !l.is_placeholder) ?? null;
  };

  const isLastLessonInChapter = () => {
    if (!lesson || !lesson.chapter) return false;

    const lessonsInChapter = courseLessons.filter(l => l.chapter === lesson.chapter && !l.is_placeholder);
    const currentIndex = lessonsInChapter.findIndex(l => l.id === lesson.id);

    return currentIndex === lessonsInChapter.length - 1;
  };

  const shouldShowChapterQuiz = () => {
    return isCompleted && lesson?.chapter && isLastLessonInChapter() && !chapterQuizCompleted.has(lesson.chapter);
  };

  const getChapterQuiz = async () => {
    if (!lesson?.chapter) return [];

    // Collect all quiz questions from all lessons in this chapter
    const lessonsInChapter = courseLessons.filter(l => l.chapter === lesson.chapter);
    const allQuestions: QuizQuestion[] = [];

    for (const chapterLesson of lessonsInChapter) {
      if (chapterLesson.quiz && Array.isArray(chapterLesson.quiz)) {
        // Add questions from this lesson
        allQuestions.push(...chapterLesson.quiz.map((q, idx) => ({
          ...q,
          explanation: q.explanation || `From: ${chapterLesson.title}`
        })));
      }
    }

    // If no questions found, create generic chapter review questions
    if (allQuestions.length === 0) {
      allQuestions.push(
        {
          question: `Chapter ${lesson.chapter} Review: What was the most interesting topic you learned?`,
          options: ['First Lesson Topic', 'Second Lesson Topic', 'Third Lesson Topic', 'All of them'],
          correctAnswer: 3,
          explanation: 'All topics contribute to your understanding of the chapter.'
        },
        {
          question: `Chapter ${lesson.chapter} Review: Are you ready to move to the next chapter?`,
          options: ['Yes, I understand everything', 'Need more review', 'Not sure yet', 'Skip for now'],
          correctAnswer: 0,
          explanation: 'Confidence in your understanding is key to progression.'
        }
      );
    }

    return allQuestions;
  };

  const handleChapterQuizAnswer = (questionIndex: number, answerIndex: number) => {
    setChapterQuizAnswers(prev => ({ ...prev, [questionIndex]: answerIndex }));
    setChapterQuizResults(prev => ({ ...prev, [questionIndex]: true }));

    const question = chapterQuizQuestions[questionIndex];
    const correctIdx = question.correct ?? question.correctAnswer ?? 0;
    const isCorrect = answerIndex === correctIdx;

    if (isCorrect) {
      toast.success('Correct! 🎉');
    } else {
      toast.error('Not quite. Review and try again!');
    }
  };

  const handleChapterQuizComplete = async () => {
    if (lesson?.is_placeholder) {
      toast.info('Placeholder chapters do not have quizzes yet.');
      return;
    }

    if (!lesson?.chapter) return;

    // Calculate score
    let correctCount = 0;
    Object.entries(chapterQuizAnswers).forEach(([qIdx, answerIdx]) => {
      const question = chapterQuizQuestions[parseInt(qIdx)];
      if (question) {
        const correctIdx = question.correct ?? question.correctAnswer ?? 0;
        if (answerIdx === correctIdx) {
          correctCount++;
        }
      }
    });

    const percentage = (correctCount / chapterQuizQuestions.length) * 100;

    // Require 70% to pass
    if (percentage >= 70) {
      // Mark chapter quiz as completed
      await supabase
        .from('user_progress')
        .update({
          quiz_completed_at: new Date().toISOString(),
          quiz_percentage: percentage,
          quiz_score: correctCount,
          quiz_total_questions: chapterQuizQuestions.length
        })
        .eq('user_id', userId)
        .eq('lesson_id', lesson.id);

      await logQuizCompleted(
        userId!,
        lesson.id,
        lesson.title,
        correctCount,
        chapterQuizQuestions.length,
        0
      );
      await incrementQuestionsAsked(chapterQuizQuestions.length);

      setChapterQuizCompleted(prev => new Set([...prev, lesson.chapter]));
      setShowChapterQuiz(false);
      setCurrentChapterQuizQuestion(0);
      setChapterQuizAnswers({});
      setChapterQuizResults({});
      toast.success(`Chapter ${lesson.chapter} completed! Score: ${Math.round(percentage)}%. Moving to next chapter...`);

      // Navigate to first lesson of next chapter
      const nextLesson = getNextLesson();
      if (nextLesson) {
        setTimeout(() => {
          navigate(`/lesson/${nextLesson.slug}/1`);
        }, 2000);
      }
    } else {
      toast.error(`You need ${70 - Math.round(percentage)}% more to pass. Review the chapter and try again!`);
    }
  };

  const handleMarkComplete = async () => {
    if (lesson?.is_placeholder) {
      toast.info('This lesson is coming soon. No XP is awarded yet.');
      return;
    }

    if (!authResolved || !userId || !lesson) {
      toast.info('Still loading your session. Please try again in a moment.');
      return;
    }

    setCompletingLesson(true);
    try {
      const { data, error: completeError } = await supabase.functions.invoke('lessons', {
        body: {
          id: lesson.id,
          action: 'complete'
        }
      });

      if (completeError) {
        console.error('[LessonPlayer] Error completing lesson:', completeError);
        toast.error('Failed to mark lesson complete');
        return;
      }

      if (data?.error) {
        console.error('[LessonPlayer] Completion function returned an error:', data.error);
        toast.error('Failed to mark lesson complete');
        return;
      }

      const awardedXp = data?.alreadyCompleted ? 0 : Number(data?.xpEarned ?? 10);

      if (data?.levelUp) {
        triggerLevelUpConfetti();
        toast.success(`🎊 Level Up! You're now level ${data.newLevel}!`);
      } else {
        triggerLessonCompleteConfetti();
      }

      if (data?.alreadyCompleted) {
        toast.info('You already completed this lesson!');
      } else {
        toast.success(`Lesson complete! +${awardedXp} XP earned! 🌟`);
      }

      setIsCompleted(true);

      const { data: updatedProfile } = await supabase
        .from('profiles')
        .select('xp_points, streak_count, level')
        .eq('user_id', userId)
        .maybeSingle();

      const totalQuestions = pages.filter(p => p.type === 'quiz' || p.type === 'fill' || (p as any).sentence !== undefined).length;
      const mistakes = 5 - hearts;
      const calculatedAccuracy = totalQuestions > 0 ? Math.round((Math.max(0, totalQuestions - mistakes) / totalQuestions) * 100) : 100;

      const completedQuests = [];
      let earnedGems = 0;
      const perfect = hearts === 5;
      if (!data?.alreadyCompleted) {
        if (perfect) {
          completedQuests.push({ id: 'quest-perfect', title: 'Perfect Recall (No mistakes!)', reward: 50 });
          earnedGems += 50;
        }
        completedQuests.push({ id: 'quest-daily', title: 'Cosmic Scholar (Complete any lesson)', reward: 20 });
        earnedGems += 20;
      }

      setCompletionData({
        xpEarned: awardedXp,
        totalXP: Number(updatedProfile?.xp_points ?? data?.stats?.xp_total ?? 0),
        accuracy: calculatedAccuracy,
        perfectLesson: perfect,
        combo: maxCombo,
        streakDays: updatedProfile?.streak_count || 1,
        streakBroken: false,
        levelUp: !!data?.levelUp,
        newLevel: updatedProfile?.level || data?.newLevel || 1,
        questsCompleted: completedQuests,
        gemsEarned: earnedGems
      });
      setLessonComplete(true);
      return;
    } catch (error) {
      console.error('Error completing lesson:', error);
      toast.error('Failed to mark lesson as complete');
    } finally {
      setCompletingLesson(false);
    }
  };

  const handleNextLesson = async () => {
    if (lesson?.is_placeholder) {
      toast.info('This lesson is coming soon. No next lesson available yet.');
      return;
    }

    // Check database to confirm current lesson is completed
    const { data: currentProgress } = await supabase
      .from('user_progress')
      .select('status')
      .eq('user_id', userId)
      .eq('lesson_id', lesson.id)
      .maybeSingle();

    const isActuallyCompleted = currentProgress?.status === 'completed';

    // If not completed, auto-complete it
    if (!isActuallyCompleted && userId && lesson) {
      await handleMarkComplete();
      // Wait a moment for the completion to process
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    const nextLesson = getNextLesson();
    if (!nextLesson) {
      toast.success('🎉 This is the last lesson! Course complete!');
      navigate(`/learning/${resolvedCourseSlug}`);
      return;
    }

    // Navigate to next lesson
    navigate(`/lesson/${nextLesson.slug}/1`);
  };

  const handleQuickNext = async () => {
    if (lesson?.is_placeholder) {
      toast.info('This lesson is coming soon. Nothing to skip forward to yet.');
      return;
    }

    // Check database to confirm current lesson is completed
    const { data: currentProgress } = await supabase
      .from('user_progress')
      .select('status')
      .eq('user_id', userId)
      .eq('lesson_id', lesson?.id)
      .maybeSingle();

    const isActuallyCompleted = currentProgress?.status === 'completed';

    // If not completed, auto-complete it
    if (!isActuallyCompleted && userId && lesson) {
      await handleMarkComplete();
      // Wait a moment for the completion to process
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    const nextLesson = getNextLesson();
    if (!nextLesson) {
      toast.success('🎉 This is the last lesson! Course complete!');
      navigate(`/learning/${resolvedCourseSlug}`);
      return;
    }

    // Navigate to next lesson
    navigate(`/lesson/${nextLesson.slug}/1`);
  };

  const handleReadAloud = async () => {
    if (!lesson) return;

    // If audio is already playing, stop it
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // If we already have the audio URL and it's loaded, just play it
    if (audioUrl && audioRef.current && audioRef.current.src) {
      audioRef.current.play();
      return;
    }

    setLoadingAudio(true);
    try {
      // Prepare text content for TTS
      const lessonText = `${lesson.title}. ${typeof lesson.content === 'string'
        ? lesson.content
        : JSON.stringify(lesson.content).substring(0, 2000)}`;

      const { data: audioBlob, error } = await supabase.functions.invoke('elevenlabs-tts', {
        body: {
          text: lessonText
        }
      });

      if (error) throw error;

      // Create blob URL from audio blob
      const url = URL.createObjectURL(audioBlob as Blob);
      setAudioUrl(url);

      toast.success('Audio generated successfully!');
    } catch (error) {
      console.error('Error generating audio:', error);
      toast.error('Failed to generate lesson audio');
    } finally {
      setLoadingAudio(false);
    }
  };

  const handleOnboardingComplete = (startingSection?: string) => {
    setShowOnboarding(false);
    setRecommendedSection(startingSection);

    if (startingSection) {
      toast.success(`Starting at ${startingSection} level based on your quiz results!`);
      // Scroll to the recommended section if needed
      setTimeout(() => {
        const element = document.getElementById(startingSection);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  };


  const resetInteractiveState = () => {
    setQuizAnswer(null);
    setQuizRevealed(false);
    setAudioPlaying(false);
    speechTokenRef.current += 1;
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    if (piperAudioRef.current) {
      piperAudioRef.current.pause();
      piperAudioRef.current.currentTime = 0;
    }
    setMatchSelectedTerm(null);
    setMatchSelectedDef(null);
    setMatchMatched(new Set());
    setMatchWrong(false);
  };

  const handleAudioToggle = () => {
    if (currentPageData?.type !== "audio") return;
    
    if (audioPlaying && piperAudioRef.current) {
      piperAudioRef.current.pause();
      setAudioPlaying(false);
      return;
    }

    if (piperAudioRef.current) {
      piperAudioRef.current.pause();
      piperAudioRef.current.currentTime = 0;
    }
    
    setAudioPlaying(true);
    const audioUrl = currentPageData.audioUrl;
    
    if (!audioUrl) {
      setAudioPlaying(false);
      toast.error("Audio URL is missing from lesson data!");
      return;
    }
    
    const audio = new Audio(audioUrl);
    audio.onended = () => setAudioPlaying(false);
    audio.onerror = () => {
      setAudioPlaying(false);
      toast.error("Audio file not generated yet! Please run scripts/generate-audio.sh");
    };
    piperAudioRef.current = audio;
    audio.play();
  };

  const playChime = (hz: number) => {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz, ctx.currentTime);

    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  };

  const [checkingAnswer, setCheckingAnswer] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [bgFlash, setBgFlash] = useState<"red" | null>(null);

  const handleQuizOptionSelect = (index: number) => {
    if (!currentPageData || currentPageData.type !== "quiz" || quizRevealed) return;
    setQuizAnswer(index);
    setMascotState("thinking");
    setMascotVariant(Math.floor(Math.random() * 5) + 1);
  };

  const handleCheckAnswer = () => {
    if (!currentPageData || quizRevealed) return;

    let isCorrect = false;

    if (currentPageData.type === "quiz") {
      if (quizAnswer === null) return;
      isCorrect = quizAnswer === currentPageData.correctIndex;
    } else if (currentPageData.type === "order") {
      if (orderCurrent.length === 0) return;
      const correct = (currentPageData as any).correctOrder || [];
      isCorrect = orderCurrent.every((val, index) => val === correct[index]);
    } else {
      return;
    }

    setQuizRevealed(true);

    if (isCorrect) {
      setMascotState("correct");
      setMascotVariant(Math.floor(Math.random() * 5) + 1);
      playChime(880);
      import("canvas-confetti").then((confetti) => {
        confetti.default({
          particleCount: 20,
          spread: 40,
          origin: { y: 0.8 },
          colors: ['#22c55e']
        });
      });
      setStreakCount(s => {
        const next = s + 1;
        setMaxCombo(m => Math.max(m, next));
        if (next > 0 && next % 4 === 0) {
          setShowStreakBanner(true);
          setTimeout(() => setShowStreakBanner(false), 1500);
        }
        return next;
      });
    } else {
      setMascotState("incorrect");
      setMascotVariant(Math.floor(Math.random() * 5) + 1);
      playChime(220);
      const optimisticHearts = Math.max(0, hearts - 1);
      setHearts(optimisticHearts);
      supabase.functions.invoke('lessons', {
        body: { action: 'deduct_heart' }
      }).then(({ data, error }) => {
        if (error) {
          console.error('[LessonPlayer] Hearts sync failed:', error);
          return;
        }
        if (typeof data?.hearts === 'number') {
          setHearts(data.hearts);
        }
      });

      setStreakCount(0);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 300);
      setBgFlash("red");
      setTimeout(() => setBgFlash(null), 150);
    }
  };

  const handlePageNext = async () => {
    if (!currentPageData) return;
    if (isMatchPage && !isMatchComplete) {
      toast.info('Match all pairs correctly to continue.');
      return;
    }

    if (!isLastPage) {
      setMascotState("idle");
      setCurrentPage((prev) => prev + 1);
      resetInteractiveState();
      return;
    }

    // Call mark complete which will show the overlay instead of navigating directly
    await handleMarkComplete();
  };

  const handlePagePrev = () => {
    if (currentPage <= 0) return;
    setCurrentPage((prev) => Math.max(0, prev - 1));
    resetInteractiveState();
  };

  if (loading || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <HelixLoader className="text-primary" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-12">
          <div className="text-center space-y-2">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground" />
            <h3 className="text-xl font-semibold">Lesson Not Found</h3>
            <Button onClick={() => navigate(`/learning/${resolvedCourseSlug}`)} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Course
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (lesson.is_placeholder) {
    return (
      <div className="container mx-auto p-6 max-w-4xl space-y-6">
        <Card className="card-cosmic">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-3xl mb-2">{lesson.title}</CardTitle>
                {lesson.chapter && (
                  <p className="text-sm text-muted-foreground">Chapter: {lesson.chapter}</p>
                )}
                <Badge variant="outline" className="mt-2">
                  Coming Soon
                </Badge>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardContent className="p-6 text-center space-y-4">
            <p className="text-lg font-semibold">This lesson is coming soon. Check back later! 🚀</p>
            <p className="text-muted-foreground">
              Placeholder lessons are visible here so you can see the roadmap. No progress or XP is tracked yet.
            </p>
            <Button variant="outline" onClick={() => navigate(`/learning/${resolvedCourseSlug}`)}>
              Back to Course
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pageProgress = totalPages > 0 ? ((currentPage + 1) / totalPages) * 100 : 0;

  const renderPage = () => {
    if (!currentPageData) return null;

    // Animate from right
    const animProps = {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.2, ease: [0.0, 0.0, 0.2, 1] }
    };

    let renderType = currentPageData.type;
    const isOriginallyFill = (currentPageData as any).sentence !== undefined;
    if (isOriginallyFill) {
      renderType = 'fill';
      if (currentPageData.type === 'fill') {
        currentPageData.type = 'quiz';
        Promise.resolve().then(() => {
          setMascotVariant(v => v === 1 ? 2 : 1);
        });
      }
      
      const fillData = currentPageData as any;
      if (fillData.correctIndex === undefined && fillData.correct !== undefined) {
        fillData.correctIndex = fillData.correct;
      }
    }

    const renderPageRawContent = () => {
      switch (renderType) {
      case "read":
        return (
          <motion.div
            key={currentPage}
            {...animProps}
            className="w-full grid grid-cols-1 md:grid-cols-12 gap-8 items-start animate-fade-in"
          >
            {/* LEFT COLUMN: 7/12 width */}
            <div className="w-full md:col-span-7 space-y-6">
              <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">{currentPageData.title}</h1>
              <div className="text-white/90">
                <LessonContent content={currentPageData.content} className="text-white text-lg leading-relaxed" />
              </div>
              
              {/* Show the simulation below the text on mobile/tablet screens only */}
              {currentPageData.simulation && (
                <div 
                  className="block md:hidden w-full flex justify-center py-4 bg-white/5 rounded-2xl border border-white/10 p-3 shadow-inner"
                  role="region"
                  aria-label={`Interactive simulation: ${lesson.title}`}
                >
                  <div style={{ width: 280, height: 280 }} className="flex items-center justify-center">
                    <SimulationErrorBoundary>
                      <SimulationRenderer lessonSlug={lesson.slug || ""} pageIndex={currentPage} pageData={currentPageData} size={280} />
                    </SimulationErrorBoundary>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: 5/12 width */}
            <div className="hidden md:flex md:col-span-5 flex-col items-center justify-center space-y-6 w-full">
              {currentPageData.simulation ? (
                <div 
                  className="w-full flex items-center justify-center bg-white/5 rounded-2xl border border-white/10 p-4 shadow-xl"
                  role="region"
                  aria-label={`Interactive simulation: ${lesson.title}`}
                >
                  <div style={{ width: 380, height: 380 }} className="flex items-center justify-center overflow-hidden rounded-xl">
                    <SimulationErrorBoundary>
                      <SimulationRenderer lessonSlug={lesson.slug || ""} pageIndex={currentPage} pageData={currentPageData} size={380} />
                    </SimulationErrorBoundary>
                  </div>
                </div>
              ) : (
                <MascotErrorBoundary>
                  <MascotDisplay state={mascotState} size={240} />
                </MascotErrorBoundary>
              )}
              <p className="text-white/40 font-medium">
                {currentPage + 1} / {pages.length}
              </p>
            </div>
          </motion.div>
        );
      case "fact":
        return (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full"
          >
            <div className="mx-auto max-w-lg rounded-2xl border-2 border-amber-400/50 bg-amber-400/10 p-8 text-center">
              <div className="mb-4 text-5xl">💡</div>
              <p className="text-xl leading-relaxed text-white">{currentPageData.text}</p>
            </div>
          </motion.div>
        );
      case "quiz": {
        const selectedIndex = quizAnswer;
        const correctIndex = currentPageData.correctIndex;
        const answerIsCorrect = selectedIndex === correctIndex;

        return (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full space-y-6"
          >
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">{currentPageData.question}</h1>

            <div className="space-y-3">
              {currentPageData.options.map((option, index) => {
                const isSelected = selectedIndex === index;
                const isCorrectOption = index === correctIndex;
                const baseClass = "w-full rounded-xl border px-5 py-4 text-left text-base transition-all duration-200";
                const variantClass = quizRevealed
                  ? isCorrectOption
                    ? "border-[#22c55e] bg-[#22c55e]/20 text-white"
                    : isSelected
                      ? "border-[#ef4444] bg-[#ef4444]/20 text-white"
                      : "border-white/10 bg-white/5 text-white/80"
                  : isSelected
                    ? "border-cyan-400 bg-cyan-400/15 text-white"
                    : "border-white/10 bg-white/5 text-white hover:border-cyan-400/50 hover:bg-white/10";

                return (
                  <Button
                    key={index}
                    type="button"
                    variant="outline"
                    disabled={quizRevealed}
                    onClick={() => handleQuizOptionSelect(index)}
                    className={`${baseClass} ${variantClass} ${isShaking && isSelected ? 'animate-shake' : ''}`}
                  >
                    <span className="flex-1">{option}</span>
                  </Button>
                );
              })}
            </div>

            {quizRevealed && (
              <div className={`rounded-xl border p-4 ${answerIsCorrect ? "border-[#22c55e]/40 bg-[#22c55e]/10" : "border-amber-400/30 bg-amber-400/10"}`}>
                <p className="text-white/90">{currentPageData.explanation}</p>
              </div>
            )}
          </motion.div>
        );
      }
      case "audio":
        return (
          <motion.div
            key={currentPage}
            {...animProps}
            className="w-full space-y-6"
          >
            <div className="mx-auto max-w-lg rounded-2xl border border-white/10 bg-[#1a1d2e] p-6">
              <p className="text-lg leading-relaxed text-white/90">{currentPageData.transcript}</p>
            </div>

            <button
              type="button"
              onClick={handleAudioToggle}
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white transition-transform hover:scale-105"
              aria-label={audioPlaying ? "Pause" : "Play"}
            >
              {audioPlaying ? (
                <svg className="h-10 w-10" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
              ) : (
                <svg className="h-10 w-10 ml-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
              )}
            </button>
          </motion.div>
        );
      case "animation":
        return (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="space-y-6 text-center"
          >
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.35em] text-amber-300/70">Animation</p>
              <Card className="mx-auto max-w-xl border-white/10 bg-white/5 text-white">
                <CardContent className="flex flex-col items-center gap-6 p-8">
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-cyan-400/15">
                    <div className="h-12 w-12 rounded-full bg-cyan-300/80 animate-pulse" />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.3em] text-white/40 mb-2">Animation ID</p>
                    <p className="text-2xl font-semibold text-white">{currentPageData.animationId}</p>
                  </div>
                  {currentPageData.caption && (
                    <p className="text-base text-white/75 max-w-md">{currentPageData.caption}</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </motion.div>
        );
      case "fill": {
        const fillData = currentPageData as any;
        const selectedIndex = quizAnswer;
        const correctIndex = fillData.correctIndex !== undefined ? fillData.correctIndex : 0;
        const answerIsCorrect = selectedIndex === correctIndex;

        const sentence = fillData.sentence || "";
        const parts = sentence.split("________");

        return (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full space-y-6"
          >
            {fillData.title && (
              <h1 className="text-2xl font-bold text-white text-center">{fillData.title}</h1>
            )}

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 container text-xl leading-relaxed text-white text-center">
              {parts[0]}
              <span className="inline-block min-w-[120px] border-b-2 border-cyan-400 mx-1 text-center text-cyan-400">
                {selectedIndex !== null ? fillData.options[selectedIndex] : "________"}
              </span>
              {parts[1]}
            </div>

            <div className="space-y-3">
              {fillData.options.map((option: string, index: number) => {
                const isSelected = selectedIndex === index;
                const isCorrectOption = index === correctIndex;
                const baseClass = "w-full rounded-xl border px-5 py-4 text-left text-base transition-all duration-200";
                const variantClass = quizRevealed
                  ? isCorrectOption
                    ? "border-[#22c55e] bg-[#22c55e]/20 text-white"
                    : isSelected
                      ? "border-[#ef4444] bg-[#ef4444]/20 text-white"
                      : "border-white/10 bg-white/5 text-white/80"
                  : isSelected
                    ? "border-cyan-400 bg-cyan-400/15 text-white"
                    : "border-white/10 bg-white/5 text-white hover:border-cyan-400/50 hover:bg-white/10";

                return (
                  <Button
                    key={index}
                    type="button"
                    variant="outline"
                    disabled={quizRevealed}
                    onClick={() => {
                      if (quizRevealed) return;
                      setQuizAnswer(index);
                      setMascotState("thinking");
                      setMascotVariant(Math.floor(Math.random() * 5) + 1);
                    }}
                    className={`${baseClass} ${variantClass} ${isShaking && isSelected ? 'animate-shake' : ''}`}
                  >
                    <span className="flex-1">{option}</span>
                  </Button>
                );
              })}
            </div>

            {quizRevealed && fillData.explanation && (
              <div className={`rounded-xl border p-4 ${answerIsCorrect ? "border-[#22c55e]/40 bg-[#22c55e]/10" : "border-amber-400/30 bg-amber-400/10"}`}>
                <p className="text-white/90">{fillData.explanation}</p>
              </div>
            )}
          </motion.div>
        );
      }
      case "order": {
        const orderData = currentPageData as any;
        const items = orderData.items || [];
        const correct = orderData.correctOrder || [];
        const answerIsCorrect = orderCurrent.every((val, index) => val === correct[index]);

        return (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full space-y-6"
          >
            {orderData.title && (
              <h1 className="text-2xl font-bold text-white text-center">{orderData.title}</h1>
            )}
            {orderData.prompt && (
              <p className="text-lg text-white/90 text-center font-medium">{orderData.prompt}</p>
            )}

            <div className="space-y-3 mx-auto max-w-xl">
              {orderCurrent.map((itemIdx, index) => {
                const itemText = items[itemIdx];

                return (
                  <motion.div
                    key={itemIdx}
                    layout
                    className="flex items-center gap-3 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left text-base text-white/90"
                  >
                    <span className="flex-1">{itemText}</span>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={index === 0 || quizRevealed}
                        onClick={() => handleMoveOrderItem(index, 'up')}
                        className="h-8 w-8 text-white/60 hover:text-white disabled:opacity-30"
                        aria-label="Move Up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={index === orderCurrent.length - 1 || quizRevealed}
                        onClick={() => handleMoveOrderItem(index, 'down')}
                        className="h-8 w-8 text-white/60 hover:text-white disabled:opacity-30"
                        aria-label="Move Down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {quizRevealed && (
              <div className={`mx-auto max-w-xl rounded-xl border p-4 ${answerIsCorrect ? "border-[#22c55e]/40 bg-[#22c55e]/10" : "border-amber-400/30 bg-amber-400/10"}`}>
                <p className="text-white/90">
                  {answerIsCorrect 
                    ? "Excellent job! You correctly ordered the sequence." 
                    : `Incorrect order. The correct sequence is:\n${correct.map((idx: number) => `• ${items[idx]}`).join('\n')}`}
                </p>
              </div>
            )}
          </motion.div>
        );
      }
      case "match": {
        const matchData = currentPageData as any;
        const pairs = matchData.pairs || [];

        // Shuffle utility
        const shuffleArray = <T,>(arr: T[]): T[] => {
          const newArr = [...arr];
          for (let i = newArr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
          }
          return newArr;
        };

        // Compute stable shuffled terms and definitions
        if (matchShuffledTerms.current.length === 0 && pairs.length > 0) {
          const termsList = pairs.map((p: any) => p.term);
          matchShuffledTerms.current = shuffleArray(termsList);
        }
        if (matchShuffledDefs.current.length === 0 && pairs.length > 0) {
          const defsList = pairs.map((p: any) => p.definition);
          matchShuffledDefs.current = shuffleArray(defsList);
        }

        const terms = matchShuffledTerms.current;
        const definitions = matchShuffledDefs.current;

        const handleTermTap = (term: string) => {
          if (matchMatched.has(term)) return;
          if (matchWrong) return;
          setMatchSelectedTerm(term);
        };

        const handleDefTap = (def: string) => {
          if (matchSelectedTerm === null) return;
          if (matchWrong) return;

          // Find if this definition is already matched
          const parentPair = pairs.find((p: any) => p.definition === def);
          if (parentPair && matchMatched.has(parentPair.term)) return;

          const correctPair = pairs.find((p: any) => p.term === matchSelectedTerm);
          const isCorrect = correctPair && correctPair.definition === def;

          if (isCorrect) {
            const nextMatched = new Set(matchMatched);
            nextMatched.add(matchSelectedTerm);
            setMatchMatched(nextMatched);
            setMatchSelectedTerm(null);
            setMatchSelectedDef(null);
            playChime(880);
          } else {
            setMatchSelectedDef(def);
            setMatchWrong(true);
            playChime(220);
            
            setTimeout(() => {
              setMatchSelectedTerm(null);
              setMatchSelectedDef(null);
              setMatchWrong(false);
            }, 400);
          }
        };

        const isAllMatched = matchMatched.size === pairs.length && pairs.length > 0;

        return (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full space-y-6"
          >
            {matchData.title && (
              <h1 className="text-2xl font-bold text-white text-center">{matchData.title}</h1>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Left Column: Terms */}
              <div className="space-y-3">
                {terms.map((term: string, idx: number) => {
                  const isTermMatched = matchMatched.has(term);
                  const isTermSelected = matchSelectedTerm === term;
                  const isTermWrong = matchWrong && isTermSelected;

                  let termCardClass = "border-white/10 bg-white/5 hover:border-cyan-400/50 hover:bg-white/10";
                  if (isTermMatched) {
                    termCardClass = "border-[#22c55e] bg-[#22c55e]/15 cursor-default";
                  } else if (isTermWrong) {
                    termCardClass = "border-red-500 bg-red-500/15";
                  } else if (isTermSelected) {
                    termCardClass = "border-cyan-400 bg-cyan-400/15";
                  }

                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={isTermMatched}
                      onClick={() => handleTermTap(term)}
                      className={`rounded-xl border px-4 py-3 text-sm font-semibold text-white cursor-pointer transition-all w-full text-center ${termCardClass}`}
                    >
                      {term}
                    </button>
                  );
                })}
              </div>

              {/* Right Column: Definitions */}
              <div className="space-y-3">
                {definitions.map((def: string, idx: number) => {
                  const parentPair = pairs.find((p: any) => p.definition === def);
                  const isDefMatched = parentPair ? matchMatched.has(parentPair.term) : false;
                  const isDefSelected = matchSelectedDef === def;
                  const isDefWrong = matchWrong && isDefSelected;

                  let defCardClass = "border-white/10 bg-white/5 hover:border-cyan-400/50 hover:bg-white/10";
                  if (isDefMatched) {
                    defCardClass = "border-[#22c55e] bg-[#22c55e]/15 cursor-default";
                  } else if (isDefWrong) {
                    defCardClass = "border-red-500 bg-red-500/15";
                  } else if (isDefSelected) {
                    defCardClass = "border-cyan-400 bg-cyan-400/15";
                  }

                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={isDefMatched}
                      onClick={() => handleDefTap(def)}
                      className={`rounded-xl border px-4 py-3 text-sm text-white/80 cursor-pointer transition-all w-full text-center ${defCardClass}`}
                    >
                      {def}
                    </button>
                  );
                })}
              </div>
            </div>

            {isAllMatched && (
              <div className="text-center font-semibold text-[#22c55e] text-lg mt-4 animate-bounce">
                All matched! ✓
              </div>
            )}
          </motion.div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <PageErrorBoundary pageType={renderType}>
      {renderPageRawContent()}
    </PageErrorBoundary>
  );
};

  if (lessonFailed) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#0B0F19] p-6 text-center">
        <div className="mb-6 rounded-full bg-red-500/20 p-6">
          <HeartCrack className="h-16 w-16 text-red-500" />
        </div>
        <h1 className="mb-4 text-3xl font-bold text-white">Out of Hearts!</h1>
        <p className="mb-8 max-w-md text-lg text-white/70">
          You've made too many mistakes. Take a break and try again later, or wait for your hearts to refill.
        </p>
        <Button
          onClick={() => navigate(courseSlug ? `/learning/${courseSlug}` : "/")}
          className="rounded-xl bg-gradient-to-r from-red-600 to-rose-600 px-8 py-6 text-lg font-bold shadow-lg transition-all hover:scale-105"
        >
          Return to Course
        </Button>
      </div>
    );
  }

  if (lessonComplete && completionData) {
    return (
      <LessonComplete
        {...completionData}
        heartsRemaining={hearts}
        onContinue={() => navigate(`/learning/${resolvedCourseSlug || 'astrophysics'}`)}
        onReviewLesson={() => setLessonComplete(false)}
      />
    );
  }

  const hasAnswerSelected = currentPageData?.type === "quiz" 
    ? quizAnswer !== null 
    : currentPageData?.type === "order" 
      ? orderCurrent.length > 0 
      : false;

  const isOrderCorrect = currentPageData?.type === "order" && (() => {
    const correct = (currentPageData as any).correctOrder || [];
    return orderCurrent.every((val, index) => val === correct[index]);
  })();

  const answerIsCorrect = currentPageData?.type === "quiz"
    ? quizAnswer === currentPageData?.correctIndex
    : isOrderCorrect;

  return (
    <div className="min-h-screen bg-[#0b0d18] text-white">
      <SEO title={lesson.title} description={`${lesson.chapter || 'Lesson'} — ${lesson.title}`}>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": lesson.title,
            "description": `${lesson.chapter || 'Lesson'} — ${lesson.title}`,
            "educationalLevel": `Grade ${getGradeLevel(lesson.slug)}`,
            "learningResourceType": "Interactive Lesson",
            "provider": {
              "@type": "Organization",
              "name": "Scico"
            }
          })}
        </script>
      </SEO>
      {/* Glassy top bar */}
      <div className="sticky top-0 z-40 bg-[#0b0d18] pt-4 pb-2">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowQuitModal(true)}
            aria-label="Quit lesson"
            className="flex-shrink-0 text-white/50 hover:bg-white/10 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </Button>

          <div className="flex-1">
            <div className="h-3 w-full bg-white/10 rounded-full overflow-hidden relative">
              <motion.div
                className="absolute left-0 top-0 bottom-0 bg-primary rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${((currentPage) / Math.max(pages.length, 1)) * 100}%` }}
                transition={{ type: "spring", stiffness: 50, damping: 15 }}
              />
            </div>
            <p className="text-center text-xs text-white/50 mt-1 font-medium">
              {currentPage + 1} / {pages.length}
            </p>
          </div>

          <div id="hearts_display" className="flex items-center gap-1 flex-shrink-0 font-bold text-red-500">
            {hearts > 0 ? (
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <motion.div
                    key={i}
                    initial={false}
                    animate={i >= hearts ? { scale: 0, opacity: 0 } : { scale: 1, opacity: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                  >
                    {i < hearts ? (
                      <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                    ) : (
                      <div className="w-5 h-5" />
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <span className="flex items-center gap-1"><Heart className="h-5 w-5 fill-red-500 text-red-500" /> 0</span>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showStreakBanner && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="fixed top-20 left-0 right-0 z-50 flex justify-center pointer-events-none"
          >
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold px-6 py-2 rounded-full shadow-lg border border-white/20 flex items-center gap-2">
              {streakCount} in a row <Flame className="w-5 h-5 fill-white text-white" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showQuitModal} onOpenChange={setShowQuitModal}>
        <DialogContent className="sm:max-w-md bg-[#1a1b26] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Quit lesson?</DialogTitle>
            <DialogDescription className="text-white/70 text-base">
              Your progress on this lesson will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowQuitModal(false);
                navigate(`/learning/${resolvedCourseSlug}`);
              }}
              className="w-full sm:w-1/2 border-white/10 bg-transparent text-white/70 hover:bg-white/5 hover:text-white"
            >
              Quit
            </Button>
            <Button
              type="button"
              onClick={() => setShowQuitModal(false)}
              className="w-full sm:w-1/2 bg-primary text-white hover:bg-primary/90"
            >
              Keep going
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main content */}
      <main className={`mx-auto flex flex-1 items-center justify-center px-4 pb-32 gap-4 ${isReadPage ? "max-w-6xl w-full" : "max-w-3xl"}`}>
        {!isReadPage && (
          <div className="hidden md:block self-start mt-16 w-16 flex-shrink-0">
            <MascotErrorBoundary>
              <MascotDisplay state={mascotState} size={64} />
            </MascotErrorBoundary>
          </div>
        )}
        <div className="w-full flex-1">
          {renderPage()}
        </div>
      </main>

      {/* Bottom page navigation (offset above the global bottom nav) */}
      <div className="fixed inset-x-0 bottom-16 z-40 border-t border-white/10 bg-[#0b0d18]/95 backdrop-blur p-4">
        <div className={`mx-auto ${isReadPage ? "max-w-6xl w-full" : "max-w-3xl"}`}>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handlePagePrev}
              disabled={currentPage === 0 || completingLesson}
              className="flex-1 rounded-xl border-white/15 bg-white/5 py-4 text-base font-semibold text-white hover:bg-white/10 disabled:opacity-40"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>

            {isQuizPage && hasAnswerSelected && !quizRevealed ? (
              <Button
                type="button"
                onClick={handleCheckAnswer}
                disabled={!authResolved || !userId || !lesson || completingLesson}
                className="flex-1 rounded-xl bg-blue-500 hover:bg-blue-600 py-4 text-base font-semibold text-white"
              >
                CHECK
              </Button>
            ) : isQuizPage && quizRevealed ? (
              <Button
                type="button"
                onClick={handlePageNext}
                disabled={completingLesson}
                className={`flex-1 rounded-xl py-4 text-base font-semibold text-white ${
                  answerIsCorrect
                    ? "bg-[#22c55e] hover:bg-[#22c55e]/90"
                    : "bg-red-500 hover:bg-red-600"
                }`}
              >
                {answerIsCorrect ? "Continue →" : "Got it →"}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handlePageNext}
                disabled={!canAdvancePage || !authResolved || !userId || !lesson || completingLesson}
                className="flex-1 rounded-xl bg-gradient-to-r from-cyan-400 to-fuchsia-500 py-4 text-base font-semibold text-white hover:opacity-95 disabled:opacity-40"
              >
                {completingLesson ? (
                  <>
                    <HelixLoader className="mr-2" />
                    Finishing...
                  </>
                ) : isLastPage ? (
                  "Finish"
                ) : (
                  "Next"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
